import { axios } from '@/plugins/axios';
import merge from 'lodash/merge';
import { Resource, Pagination, Statement } from '@/helpers/Interfaces';
import Pager from '@/helpers/Pager';
import { DateTime } from 'luxon';
import { BorderStyleEnum, TextDecorationStyleEnum } from '@/helpers/Enums';

/**
 * BannersService
 */
export default class LabelsService
{
    /**
     * @param filter FilterModel
     * @param pager Pager
     *
     * @returns Promise<Pagination<Resource<ListItemModel>>>
     */
    public static async getList(filter: FilterModel, pager: Pager): Promise<Pagination<Resource<ListItemModel>>>
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>(`admin/store/labels`, {
            params: merge({}, filter, pager.data())
        })).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<Resource<FormModel>>
     */
    public static async fetch(id: number): Promise<Resource<FormModel>>
    {
        return (await axios.get<Resource<FormModel>>(`admin/store/labels/${id}`)).data;
    }

    /**
     * @param id number
     * @param model UpsertModel
     *
     * @returns Promise<Statement>
     */
    public static async create(model: FormModel):  Promise<ResponseModel>
    {
        return (await axios.post(`admin/store/labels`, model)).data as ResponseModel;
    }

    /**
     * @param id number
     * @param model UpsertModel
     *
     * @returns Promise<Statement>
     */
    public static async update(id: number, model: FormModel):  Promise<ResponseModel>
    {
        return (await axios.put(`admin/store/labels/${id}`, model)).data as ResponseModel;
    }

    /**
     * @param id number
     *
     * @returns Promise<Statement>
     */
    public static async remove(id: number): Promise<Statement>
    {
        return (await axios.delete(`admin/store/labels/${id}`)).data as Statement;
    }

    /**
     * @param search string
     * @param limit number
     *
     * @returns Promise<AutocompleteListModel[]>
     */
    public static async getAutocompleteList(search: string, limit: number = 10, excluded: number[] = []): Promise<AutocompleteListModel[]>
    {
        return (await axios.get<AutocompleteListModel[]>('admin/store/labels/autocomplete', {
            params: {
                search: search,
                limit: limit,
                excluded: excluded
            }
        })).data;
    }

    /**
    * @param id number
    *
    * @returns Promise<AutocompleteListModel>
    */
    public static async fetchAutocompleteModel(id: number): Promise<AutocompleteListModel[]>
    {
        return (await axios.get<AutocompleteListModel[]>(`admin/store/labels/autocomplete/${id}`)).data;
    }

    /**
    * @param labelsIds number[]
    *
    * @returns Promise<AutocompleteListModel>
    */
    public static async getListByIds(labelsIds: number[]): Promise<AutocompleteListModel[]>
    {
        return (await axios.get<AutocompleteListModel[]>(`admin/store/labels/ids`, {
            params: {
                labelsIds: labelsIds
            }
        })).data;
    }
}

export interface ListItemModel
{
    id: number;
    createdBy: string;
    dateModifiedUtc?: DateTime;
    name: string;
    dateFormUtc?: DateTime;
    dateDueUtc?: DateTime;
}

export interface FormModel
{
    id: number;
    name: string;
    displayName: Record<string, string>;
    dateFromUtc?: DateTime;
    dateDueUtc?: DateTime;
    backgroundColor: string;
    borderColor: string;
    borderSize?: number;
    borderStyle: BorderStyleEnum;
    isTextBold: boolean;
    isTextItalic: boolean;
    isTextLineThrough: boolean;
    isTextOverline: boolean;
    isTextUnderline: boolean;
    paddingHorizontal?: number;
    paddingVertical?: number;
    priority: boolean;
    textColor: string;
    textDecorationColor: string;
    textDecorationStyle: TextDecorationStyleEnum;
    textSize?: number;
}

export interface FilterModel
{
    name: string;
    dateModifiedFromUtc?: DateTime,
    dateModifiedDueUtc?: DateTime
}

export interface AutocompleteListModel
{
    id: number;
    name: string;
}

export interface ResponseModel
{
    id: number;
}
